import React from 'react'
import styled from 'styled-components'
import { line, radius, font, surface } from 'features/dashboard/tokens'
import { scoreColour } from 'features/dashboard/charts/scoreColour'

const pct = v => (v === null || v === undefined ? 'No score' : `${Math.round(v)}%`)

// trend: [{ label, on_time_rate, due, completed }] — compares last two entries
const PeriodDelta = ({ trend = [] }) => {
  const pts = (trend || []).filter(Boolean)
  if (pts.length < 2) {
    return <Empty>Not enough history yet to compare periods.</Empty>
  }
  const curr = pts[pts.length - 1]
  const prev = pts[pts.length - 2]

  const rows = [
    { key: 'ontime', label: 'On-time rate', c: curr.onTimeRate, p: prev.onTimeRate, isPct: true },
    { key: 'completed', label: 'Completed', c: curr.completed, p: prev.completed, isPct: false },
    { key: 'due', label: 'Due', c: curr.due, p: prev.due, isPct: false },
  ]

  return (
    <Wrap>
      <Head>
        <HeadMonth>{curr.label}</HeadMonth>
        <HeadVs>vs {prev.label}</HeadVs>
      </Head>
      <List>
        {rows.map(r => {
          const delta = (r.c ?? 0) - (r.p ?? 0)
          const arrow = delta > 0 ? '▲' : delta < 0 ? '▼' : '◆'
          const good = delta >= 0
          return (
            <Row key={r.key}>
              <RowLabel>{r.label}</RowLabel>
              <RowNow $tint={r.isPct ? scoreColour(r.c) : undefined}>{r.isPct ? pct(r.c) : (r.c ?? 0)}</RowNow>
              <Delta $good={good} $flat={delta === 0}>
                {arrow} {Math.abs(Math.round(delta))}{r.isPct ? 'pt' : ''}
              </Delta>
            </Row>
          )
        })}
      </List>
    </Wrap>
  )
}

export default PeriodDelta

const Wrap = styled.div`
  display: flex; flex-direction: column; gap: 12px;
`

const Head = styled.div`
  display: flex; align-items: baseline; gap: 8px;
`

const HeadMonth = styled.span`
  font-size: ${font.emphasis};
  font-weight: 800;
  color: ${({ theme }) => theme.colors.haiti};
`

const HeadVs = styled.span`
  font-size: ${font.caption};
  color: ${({ theme }) => theme.colors.grey[800]};
`

const List = styled.div`
  display: flex; flex-direction: column;
`

const Row = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 9px 4px;
  border-bottom: 1px solid ${surface.wash};
  &:last-child { border-bottom: none; }
`

const RowLabel = styled.span`
  flex: 1; min-width: 0;
  font-size: ${font.body};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const RowNow = styled.span`
  font-size: ${font.emphasis};
  font-weight: 800;
  color: ${({ $tint, theme }) => $tint || theme.colors.haiti};
  font-variant-numeric: tabular-nums;
`

const Delta = styled.span`
  min-width: 58px; text-align: right;
  font-size: ${font.caption};
  font-weight: 800;
  color: ${({ $good, $flat }) => ($flat ? '#64748b' : $good ? '#15803d' : '#b00020')};
`

const Empty = styled.div`
  padding: 24px; text-align: center;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-size: ${font.body};
  background: ${({ theme }) => theme.colors.lighterGrey};
  border: 1px dashed ${line.base};
  border-radius: ${radius.md};
`
